import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";

import { useAuthedApi } from "@/api/useAuthedApi";
import type { BillingPlan } from "@/api/billingApi";
import { useOnboardingState } from "./useOnboardingState";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? "");

/** Step 3: Choose a plan and pay via Stripe Elements (SetupIntent flow). */
export function PlanStep() {
  const { billing } = useAuthedApi();
  const { selectedPlanId, selectedCycle, setSelectedPlanId, setSelectedCycle } =
    useOnboardingState();

  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch plans on mount
  useEffect(() => {
    billing
      .plans()
      .then(setPlans)
      .catch(() => setError("Failed to load plans"))
      .finally(() => setLoading(false));
  }, [billing]);

  // When a plan is selected, create a SetupIntent
  const handleSelectPlan = useCallback(
    async (planId: string) => {
      setSelectedPlanId(planId);
      setError(null);
      try {
        const { client_secret } = await billing.setupIntent();
        setClientSecret(client_secret);
      } catch {
        setError("Failed to initialize payment. Please try again.");
      }
    },
    [billing, setSelectedPlanId],
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Choose your plan
      </Typography>

      {error && <Alert severity="error">{error}</Alert>}

      {/* Billing cycle toggle */}
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <ToggleButtonGroup
          value={selectedCycle}
          exclusive
          onChange={(_, v) => v && setSelectedCycle(v)}
          size="small"
        >
          <ToggleButton value="monthly">Monthly</ToggleButton>
          <ToggleButton value="annual">Annual (save ~28%)</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Plan cards */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        {plans.map((plan) => {
          const price =
            selectedCycle === "annual"
              ? plan.price_annual_cents
              : plan.price_monthly_cents;
          const perMonth =
            selectedCycle === "annual"
              ? Math.round(price / 12 / 100)
              : Math.round(price / 100);
          const isSelected = selectedPlanId === plan.id;

          return (
            <Card
              key={plan.id}
              variant={isSelected ? "elevation" : "outlined"}
              sx={{
                flex: 1,
                cursor: "pointer",
                border: isSelected ? "2px solid" : undefined,
                borderColor: isSelected ? "primary.main" : undefined,
              }}
              onClick={() => handleSelectPlan(plan.id)}
            >
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {plan.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {plan.description}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  ${perMonth}
                  <Typography component="span" variant="body2" color="text.secondary">
                    /mo
                  </Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {plan.seat_limit ? `Up to ${plan.seat_limit} seats` : "Unlimited seats"}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      {/* Payment form (shown after plan selection) */}
      {clientSecret && selectedPlanId && (
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <PaymentForm
            planId={selectedPlanId}
            cycle={selectedCycle}
          />
        </Elements>
      )}
    </Stack>
  );
}

/** Inner payment form using Stripe Elements hooks. */
function PaymentForm({
  planId,
  cycle,
}: {
  planId: string;
  cycle: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { billing } = useAuthedApi();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setError(null);

    try {
      // Confirm the SetupIntent to get the payment method
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
        confirmParams: { return_url: window.location.href },
      });

      if (confirmError) {
        setError(confirmError.message ?? "Payment confirmation failed");
        setSubmitting(false);
        return;
      }

      if (!setupIntent?.payment_method) {
        setError("No payment method returned");
        setSubmitting(false);
        return;
      }

      // Subscribe with the confirmed payment method
      await billing.subscribe({
        plan_id: planId,
        billing_cycle: cycle,
        payment_method_id: setupIntent.payment_method as string,
      });

      // Refresh so /auth/me returns the new onboarding_step
      window.location.assign("/onboard");
    } catch (err) {
      setError((err as Error).message || "Subscription failed. Please try again.");
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={2} sx={{ mt: 2 }}>
        <PaymentElement />
        {error && <Alert severity="error">{error}</Alert>}
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={!stripe || submitting}
          fullWidth
        >
          {submitting ? <CircularProgress size={20} /> : "Subscribe & Continue"}
        </Button>
      </Stack>
    </form>
  );
}
